import { useChargeMemberStore } from "@/stores/useChargeMemberStore";
import { usePaymentBreakdownMutation } from "@/stores/queries/payments.query";
import { convertToMonetaryAmount } from "@/utils/numbers.utils";
import { toast } from "@/utils/toast.utils";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useMemberDetails } from "./useMemberDetails";

export function useChargeMemberFlow(memberId: string) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { fetchMemberDetails } = useMemberDetails(memberId);
  // Get store actions and state
  const {
    currentStep,
    requestAmount,
    requestTip,
    paymentData,
    otpPaymentId,
    successData,
    setPaymentData,
    handleBack,
    handleRequestOtp,
    handleVerificationSuccess,
    reset,
  } = useChargeMemberStore();

  // Get the payment breakdown mutation
  const { mutate: calculatePaymentBreakdown, isPending: isSubmittingPayment } =
    usePaymentBreakdownMutation();

  // Handle form submission
  const handleSubmit = (data: {
    memberId: string;
    locationId: string;
    amountToPay: number;
    tipAmount: number;
    note: string;
  }) => {
    // Call the API with the converted monetary values
    calculatePaymentBreakdown(
      {
        memberId: data.memberId,
        locationId: data.locationId,
        amountToPay: convertToMonetaryAmount(data.amountToPay),
        tipAmount: convertToMonetaryAmount(data.tipAmount),
        note: data.note,
      },
      {
        onSuccess: (responseData) => {
          // Set payment data in the store
          setPaymentData(data.amountToPay, data.tipAmount, {
            ...responseData,
            memberId: data.memberId,
            locationId: data.locationId,
            note: data.note,
          });
        },
        onError: () => {
          toast.error(t("charge.paymentCalculationError"));
        },
      }
    );
  };

  // Handle done (navigation back to member details)
  const handleDone = () => {
    reset(); // Reset store state
    fetchMemberDetails(); // Refetch member details to get the latest transactions
    navigate(`/members/${memberId}`);
  };

  useEffect(() => {
    return () => reset();
  }, []);

  return {
    // State
    currentStep,
    requestAmount,
    requestTip,
    paymentData,
    otpPaymentId,
    successData,
    isSubmittingPayment,

    // Actions
    handleSubmit,
    handleBack,
    handleRequestOtp,
    handleVerificationSuccess,
    handleDone,
    reset,
  };
}
